import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import OpenInFullOutlinedIcon from '@mui/icons-material/OpenInFullOutlined';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';

import { themeColors } from '@/config/theme/themeColors.ts';
import { ResolvedAnnouncement } from '@/contexts/announcements/model/Announcements.model.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  announcements: ResolvedAnnouncement[];
  isOpen: boolean;
  onClose: () => void;
  onPreview: (announcementId: string) => void;
};

export const AnnouncementsPanel: React.FC<Props> = ({
  announcements,
  isOpen,
  onClose,
  onPreview,
}) => {
  const { t } = useTranslationWithPrefix('components.announcements');

  return (
    <Drawer anchor="right" onClose={onClose} open={isOpen}>
      <Stack
        spacing={3}
        sx={{
          background: 'linear-gradient(180deg, rgba(247,247,248,1) 0%, rgba(255,255,255,1) 100%)',
          height: '100%',
          p: 3,
          width: { sm: 420, xs: 'min(100vw, 420px)' },
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <AutoAwesomeOutlinedIcon sx={{ color: themeColors.accentColorDark }} />
              <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{t('panel.title')}</Typography>
            </Stack>
            <Button onClick={onClose} variant="text">
              {t('panel.close')}
            </Button>
          </Stack>
          <Typography sx={{ color: themeColors.greyDark }}>{t('panel.subtitle')}</Typography>
        </Stack>
        <Divider />
        <Stack spacing={2}>
          {announcements.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                backgroundColor: themeColors.white,
                border: `1px dashed ${themeColors.greyLight}`,
                p: 2,
              }}
            >
              <Typography sx={{ color: themeColors.greyDark }}>{t('panel.empty')}</Typography>
            </Paper>
          )}
          {announcements.map(announcement => (
            <Paper
              elevation={0}
              key={announcement.announcementId}
              sx={{
                backgroundColor: themeColors.white,
                border: `1px solid ${themeColors.stroke}`,
                p: 2,
              }}
            >
              <Stack spacing={1.5}>
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {announcement.title}
                  </Typography>
                  <Chip
                    color={announcement.isSeen ? 'default' : 'secondary'}
                    label={announcement.isSeen ? t('status.seen') : t('status.new')}
                    size="small"
                    sx={{ fontWeight: 600 }}
                    variant={announcement.isSeen ? 'outlined' : 'filled'}
                  />
                </Stack>
                <Typography sx={{ color: themeColors.greyDark }}>
                  {announcement.description}
                </Typography>
                <Button
                  disabled={!announcement.isAvailable}
                  endIcon={<OpenInFullOutlinedIcon />}
                  onClick={() => onPreview(announcement.announcementId)}
                  sx={{ alignSelf: 'flex-start' }}
                  variant="outlined"
                >
                  {announcement.isAvailable ? t('panel.preview') : t('panel.unavailable')}
                </Button>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </Drawer>
  );
};
